import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  inject,
  Input,
  Signal,
  TemplateRef,
  ViewChild,
  ElementRef,
  effect,
  AfterViewInit,
} from '@angular/core';
import WaveSurfer from 'wavesurfer.js';
import { MediaPlayerFile } from '../../../../interfaces/media-player-store.interface';
import { onComparerMediaPlayerAction } from '../../../../services/comments.service';
import { Subject, takeUntil } from 'rxjs';
import { MediaPlayerStoreService, MediaPlayerStore } from '../../../../services/media-player-store.service';
import { v4 as uuid4 } from 'uuid';
import { currentAppToken } from '../../../../auth/app-token';

@Component({
  selector: 'audio-viewer',
  templateUrl: './audio-viewer.component.html',
  styleUrl: './audio-viewer.component.scss',
  standalone: false,
})
export class AudioViewerComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() instanceId: string;
  @Input() assetListTemplate!: TemplateRef<any>;
  @Input() assetInfoTemplate!: TemplateRef<any>;
  @Input() audioPreloadMethod: string = 'metadata';

  private readonly destroy$: Subject<void> = new Subject<void>();
  private readonly mpStoreService: MediaPlayerStoreService = inject(MediaPlayerStoreService);
  mpStore: MediaPlayerStore | null = null;

  // Direct signals from mpStore
  readonly projectIdSignal: Signal<string | null> = computed(() => this.mpStore?.projectIdSignal() ?? null);
  readonly sectionIdSignal: Signal<string | null> = computed(() => this.mpStore?.sectionIdSignal() ?? null);
  readonly subsectionIdSignal: Signal<string | null> = computed(() => this.mpStore?.subsectionIdSignal() ?? null);
  readonly subsectionStateSignal: Signal<any | null> = computed(() => this.mpStore?.subsectionStateSignal() ?? null);
  readonly typeIdSignal: Signal<string | null> = computed(() => this.mpStore?.typeIdSignal() ?? null);
  readonly instanceIdSignal: Signal<string> = computed(() => this.mpStore?.instanceIdSignal() ?? '');
  readonly itemSignal: Signal<MediaPlayerFile | null> = computed(() => this.mpStore?.itemSignal() ?? null);
  readonly projectParticipantsSignal: Signal<unknown[]> = computed(
    () => this.mpStore?.projectParticipantsSignal() ?? []
  );
  readonly annotationsSignal: Signal<unknown[]> = computed(() => this.mpStore?.annotationsSignal() ?? []);
  readonly userInfoSignal: Signal<unknown> = computed(() => this.mpStore?.userInfoSignal() ?? null);
  readonly parentElementContainerSignal: Signal<string | null> = computed(
    () => this.mpStore?.parentElementContainerSignal() ?? null
  );
  readonly enableResponsiveSignal: Signal<boolean> = computed(() => this.mpStore?.enableResponsiveSignal() ?? false);
  readonly audioGapSignal: Signal<number> = computed(() => this.mpStore?.audioGapSignal() ?? 52);
  readonly portfolioPreviewSignal: Signal<boolean> = computed(() => this.mpStore?.portfolioPreviewSignal() ?? false);
  readonly audioViewerActionSignal: Signal<string | null> = computed(
    () => this.mpStore?.audioViewerActionSignal() ?? null
  );
  readonly hideAnnotationToolbarSignal: Signal<boolean> = computed(
    () => this.mpStore?.hideAnnotationToolbarSignal() ?? false
  );
  readonly hasEditPermissionSignal: Signal<boolean> = computed(() => this.mpStore?.hasEditPermissionSignal() ?? false);
  readonly enableAnnotationSignal: Signal<boolean> = computed(() => this.mpStore?.enableAnnotationSignal() ?? false);

  @ViewChild('mtmAudioPlayer', { static: false })
  set mtmAudioPlayerRef(ref: ElementRef<HTMLAudioElement> | undefined) {
    if (ref) {
      this._mtmAudioPlayer = ref;
      this.initializeAudioElementIfReady();
    }
  }
  get mtmAudioPlayer(): ElementRef<HTMLAudioElement> | undefined {
    return this._mtmAudioPlayer;
  }
  private _mtmAudioPlayer?: ElementRef<HTMLAudioElement>;

  uuid: string = uuid4();
  wavesurfer: any;
  private isInitializationPending: boolean = false;

  constructor() {
    // Watch for audio viewer actions from service - triggers immediately when action is set
    effect(() => {
      const action: string | null = this.audioViewerActionSignal();
      if (action === 'prepareWaveSurfer') {
        this.isInitializationPending = true;
        this.initializeAudioElementIfReady();
        this.mpStore?.updateStore({ audioViewerAction: null });
      }
    });
  }

  ngOnInit(): void {
    this.mpStore = this.mpStoreService.getStore(this.instanceId);
    if (!this.mpStore) {
      throw new Error(`Failed to get media player store for instanceId: ${this.instanceId}`);
    }
  }

  ngAfterViewInit(): void {
    onComparerMediaPlayerAction.pipe(takeUntil(this.destroy$)).subscribe({
      next: (action: { action: string }) => this.handleComparerAction(action.action),
    });
    // Try to initialize if element is already available
    this.initializeAudioElementIfReady();
  }

  private handleComparerAction(action: string): void {
    const audio: HTMLAudioElement | undefined = this.mtmAudioPlayer?.nativeElement;
    if (!audio) return;
    const actions: Record<string, () => void> = {
      setToStart: () => (audio.currentTime = 0),
      setToEnd: () => (audio.currentTime = audio.duration),
      pause: () => audio.pause(),
      play: () => audio.play(),
      mute: () => (audio.muted = true),
      unmute: () => (audio.muted = false),
    };
    actions[action]?.();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.mpStore?.updateStore({ annotationPlugin: null });
    if (this.wavesurfer) {
      this.wavesurfer.destroy();
      this.wavesurfer = null;
    }
  }
  /**
   * Initialize audio element when it becomes available in the DOM
   * This method is called when ViewChild detects the element and when initialization is pending
   */
  private initializeAudioElementIfReady(): void {
    if (!this.isInitializationPending || !this.mtmAudioPlayer?.nativeElement) {
      return;
    }

    if (!this.mpStore) return;

    const item: MediaPlayerFile | null = this.mpStore.itemSignal();
    if (!item) {
      return;
    }

    const audioElement: HTMLAudioElement = this.mtmAudioPlayer.nativeElement;

    // Ensure element is in the DOM
    if (!audioElement.isConnected) {
      return;
    }

    this.isInitializationPending = false;
    this.prepareWaveSurfer();
  }

  /**
   * Prepare WaveSurfer for audio playback without annotation mode
   * Loading waveform/ previewing audio file without annotation mode
   */
  private prepareWaveSurfer(): void {
    if (!this.mpStore || !this.mtmAudioPlayer?.nativeElement) return;

    const item: MediaPlayerFile | null = this.mpStore.itemSignal();
    if (!item) {
      return;
    }

    const audioElement: HTMLAudioElement = this.mtmAudioPlayer.nativeElement;
    audioElement.src = item.signedURL;
    audioElement.load();

    const token: string = currentAppToken();
    let height: number;
    if (this.mpStore.portfolioPreviewSignal()) {
      height = 70;
    } else {
      height = 200;
    }
    this.mpStore.updateStore({ isAudioPluginReady: false });

    // Destroy existing wavesurfer instance if it exists
    if (this.wavesurfer) {
      this.wavesurfer.destroy();
    }

    const containerElement: HTMLElement | null = document.getElementById(this.uuid);
    if (!containerElement) {
      return;
    }

    this.wavesurfer = WaveSurfer.create({
      container: containerElement,
      waveColor: '#909091',
      progressColor: '#fe3b00',
      barWidth: 1,
      cursorColor: 'transparent',
      height: height,
      fetchParams: {
        headers: [['Authorization', 'Bearer ' + token]],
      },
    });

    this.wavesurfer.on('ready', () => {
      this.mpStore?.updateStore({ isAudioPluginReady: true });
    });

    this.wavesurfer.load(item.signedURL);

    // Use native event listener instead of jQuery
    audioElement.addEventListener('timeupdate', (e: Event) => {
      const target: HTMLAudioElement = e.target as HTMLAudioElement;
      if (this.wavesurfer && target.currentTime !== undefined && this.wavesurfer.setCurrentTime) {
        this.wavesurfer.setCurrentTime(target.currentTime);
      }
    });
  }
}
